import React, { memo } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { CalendarEvent, EEventTypes, EEventStatus, CompanyEvent } from '../../types/events';
import ProfileImage from '@common/components/profile-image';
import Icon, { IconTypes } from '@common/components/icon';

interface EventItemProps {
  item: CalendarEvent;
  onEventClick?: (event: CalendarEvent) => void;
}

enum IconTypesLookup {
  default = '',
  check_circle = 'check_circle',
  calendar_today = 'calendar_today',
  access_time = 'access_time',
  refresh = 'refresh',
  approved = 'approved',
  accepted = 'accepted',
  rejected = 'rejected',
  awaiting_approval = 'awaiting_approval',
  expired = 'expired',
  open = 'open',
  event_canceled = 'event_canceled',
  going = 'going',
}

function EventItem({ item, onEventClick }: EventItemProps) {
  const { t } = useTranslation();

  const getTitle = (event: CalendarEvent) => {
    if (event.title) return event.title;
    if (event.start_time && event.end_time) {
      const startTime = moment(event.start_time).format('HH:mm');
      const endTime = moment(event.end_time).format('HH:mm');
      return `${startTime} - ${endTime}`;
    }
    if (event.start_time) {
      return moment(event.start_time).format('HH:mm');
    }


    return event.title || '';
  };

  const commonColor = `tw-relative before:tw-content-[""] before:tw-absolute before:tw-left-[6px]
   before:tw-top-[7%] before:tw-w-[4px] before:tw-h-[86%] before:tw-rounded-md tw-pl-5`;
  const greenColor = `${commonColor} before:tw-bg-green-500`;
  const redColor = `${commonColor} before:tw-bg-red-500`;
  const orangeColor = `${commonColor} before:tw-bg-orange-500`;
  const cyanColor = `${commonColor} before:tw-bg-cyan-500`;
  const indigoColor = (value?: boolean) => (value ? 'tw-text-indigo-500' : `${commonColor} before:tw-bg-indigo-500`);
  const pinkColor = (value?: boolean) => (value ? 'tw-text-pink-500' : `${commonColor} before:tw-bg-pink-500`);
  const blueColor = `${commonColor} before:tw-bg-blue-500`;
  const purpleColor = `${commonColor} before:tw-bg-purple-500`;
  const commonIcon = 'tw-absolute tw-bottom-[-4px] tw-right-0 tw-p-0.5 tw-rounded-full';
  const IconDisplay = ({ type, isFilled = false, className }:
      { type: IconTypes, className: string, isFilled?: boolean }) => {
    return (
      <span className={`${commonIcon} ${className}`}>
        <Icon size={12} type={type} isFilled={isFilled} className=" tw-text-white" />
      </span>
    );
  };


  const getIcon = (type: IconTypesLookup) => {
    switch (type) {
      case IconTypesLookup.check_circle:
        return <IconDisplay type="check_circle" isFilled className=" tw-bg-green-500" />;
      case IconTypesLookup.calendar_today:
        return <IconDisplay type="calendar_today" isFilled className=" tw-bg-red-500" />;
      case IconTypesLookup.access_time:
        return <IconDisplay type="access_time" className=" tw-bg-orange-500" />;
      case IconTypesLookup.refresh:
        return <IconDisplay type="refresh" className=" tw-bg-green-500" />;
      case IconTypesLookup.approved:
        return (
          <span className="tw-bg-green-500 tw-text-white tw-rounded-full tw-p-[3px]">
            <Icon size={10} type="check_tick" />
          </span>
        );
      case IconTypesLookup.awaiting_approval:
        return <Icon size={16} type="access_time" className=" tw-bg-orange-500 tw-text-white tw-rounded-full" />;
      case IconTypesLookup.expired:
        return <Icon size={16} type="close_round__filled" className=" tw-bg-red-500 tw-text-white tw-rounded-full" />;
      case IconTypesLookup.rejected:
        return <Icon size={16} type="close_round__filled" className=" tw-bg-red-500 tw-text-white tw-rounded-full" />;
      case IconTypesLookup.accepted:
        return (
          <span className="tw-bg-green-500 tw-text-white tw-rounded-full tw-p-0.5">
            <Icon size={16} type="check_circle" isFilled />
          </span>
        );
      case IconTypesLookup.open:
        return <Icon size={16} type="circle" className=" tw-bg-cyan-500 tw-text-white tw-rounded-full" />;
      case IconTypesLookup.event_canceled:
        return <Icon size={16} type="close_round__filled" className="tw-text-gray-500" />;
      case IconTypesLookup.going:
        return <Icon size={16} type="check_circle" isFilled className="tw-text-gray-500" />;
      default:
        return <IconDisplay type="calendar_today" isFilled className=" tw-bg-cyan-500" />;
    }
  };

  const getStatus = () => {
    switch (item.type) {
      case EEventTypes.EXCHANGE:
        const exchangeEvent = item as any;
        switch (exchangeEvent.status) {
          case EEventStatus.APPROVED:
            return {
              icon: getIcon(IconTypesLookup.refresh),
              statusIcon: getIcon(IconTypesLookup.approved),
              className: greenColor,
            };
          case EEventStatus.REJECTED:
            return {
              icon: getIcon(IconTypesLookup.calendar_today),
              statusIcon: getIcon(IconTypesLookup.rejected),
              className: redColor,
            };
          case EEventStatus.AWAITING_APPROVAL:
            return {
              icon: getIcon(IconTypesLookup.access_time),
              statusIcon: getIcon(IconTypesLookup.awaiting_approval),
              className: orangeColor,
            };
          case EEventStatus.ACCEPTED:
            return {
              icon: getIcon(IconTypesLookup.check_circle),
              statusIcon: getIcon(IconTypesLookup.accepted),
              className: greenColor,
            };
          case EEventStatus.EXPIRED:
            return {
              icon: getIcon(IconTypesLookup.calendar_today),
              statusIcon: getIcon(IconTypesLookup.expired),
              className: redColor,
            };
          default:
            return {
              icon: getIcon(IconTypesLookup.default),
              statusIcon: getIcon(IconTypesLookup.open),
              className: cyanColor,
            };
        }
      case EEventTypes.BIRTHDAY:
        return {
          className: indigoColor(),
        };
      case EEventTypes.ANNIVERSARY:
        return {
          className: pinkColor(),
        };
      case EEventTypes.SHIFT:
        return {
          className: blueColor,
        };
      case EEventTypes.EVENT:
        return {
          statusIcon: item.status === 'CANCELED' ? getIcon(IconTypesLookup.event_canceled)
          : item.is_going ? getIcon(IconTypesLookup.going) : null,
          className: purpleColor,
        };
      default:
        return {
          className: null,
        };
    }
  };

  const handleClick = () => {
    if (onEventClick) {
      onEventClick(item);
    }
  };

  const title = getTitle(item);
  const { className, icon, statusIcon } = getStatus();

  const eventTime = (event: CalendarEvent) => {
    if (event.start_time && event.end_time) {
      const eventData = event as CompanyEvent;
      const startTime = moment(event.start_time).format('HH:mm');
      const endTime = moment(event.end_time).format('HH:mm');
      const endDate = moment(event.end_time).format('MMM DD');
      const isSameDay = moment(event.start_time).isSame(
        moment(event.end_time),
        'day'
      );
      return eventData.is_full_day_event
        ? { start: t('events:forms_event_full_day'), end: '', isSameDay }
        : {
          start: startTime + (isSameDay ? '' : ` ${t('core:user_status_until')}`),
          end: `${isSameDay ? '' : endDate + ', '}${endTime}`,
          isSameDay
        };
    }
    return { start: '', end: '', isSameDay: false };
  };


  const getUserRender = () => {
    switch (item.type) {
      case EEventTypes.BIRTHDAY:
      case EEventTypes.ANNIVERSARY:
        return (
          <div className="tw-flex tw-gap-2">
            {(item as any).users.slice(0, 3).map((u: any) => (
              <ProfileImage key={u.id} size={26} user={u} enablePopover />
            ))}
            {(item as any).users.length > 3 && (
              <span className="tw-text-gray-500">
                +{(item as any).users.length - 3}
              </span>
            )}
          </div>
        );
      case EEventTypes.EVENT:
        return (
          <div className="tw-flex tw-gap-2 tw-items-center">
            {(item as any).attendees.slice(0, 3).map((u: any) => (
              <ProfileImage key={u.id} size={26} user={u} enablePopover />
            ))}
            {(item as any).attending_users_count > 0 && (
              <span className="tw-text-gray-500">
                {t('calendar:going_count', { count: (item as any).attending_users_count })}
              </span>
            )}
          </div>
        );
      default:
        return (
          item.user && (
            <ProfileImage size={25} user={item.user as any} enablePopover />
          )
        );
    }
  };
  const titleStyle = 'tw-text-base';

  const getItemsRender = () => {
    switch (item.type) {
      case EEventTypes.BIRTHDAY:
      case EEventTypes.ANNIVERSARY:
        return (
          <>
            <div className={`${titleStyle} 
            ${EEventTypes.BIRTHDAY === item.type ? indigoColor(true) : pinkColor(true)}`}
            >
              {title}
            </div>
            {getUserRender()}
          </>
        );
      case EEventTypes.EVENT:
        const eventTimeData: { start: string, end?: string, isSameDay: boolean } = eventTime(item);
        return (
          <>
            <div className="tw-flex tw-justify-between tw-gap-1">
              <div
                className={`${titleStyle} tw-line-clamp-2 tw-break-words tw-max-w-[150px]`}
              >
                {title}
              </div>
              <div className="tw-flex tw-flex-col tw-gap-1 ">
                <div className={`tw-text-sm tw-text-end tw-whitespace-nowrap
                  ${item.status === 'CANCELED' ? 'tw-line-through' : ''}`}
                >
                  {eventTimeData.start}
                </div>
                {eventTimeData.end && (
                  <div className={`tw-text-sm tw-text-gray-500 tw-whitespace-nowrap 
                  ${item.status === 'CANCELED' ? 'tw-line-through' : ''}`}
                  >
                    {eventTimeData.end}
                  </div>
                )}
              </div>
            </div>
            {item.status === 'CANCELED' ? (
              <div className="tw-text-sm tw-flex tw-gap-1 tw-items-center">
                {statusIcon}
                <span>{t('calendar:cancelled')}</span>
              </div>
            ) : !item.settings?.disable_guest_list &&
                !item.settings?.hide_guest_list ? (
                  <>
                    {item.is_going && (
                      <div className="tw-text-sm tw-flex tw-gap-1 tw-items-center">
                        {statusIcon}
                        <span>{t('calendar:going')}</span>
                      </div>
                    )}
                    {getUserRender()}
                  </>
            ) : null}
          </>
        );
      case EEventTypes.SHIFT:

        return (
          <>
            <div
              className={`${titleStyle}`}
            >
              {title}
            </div>
            {item.network_name && (
              <div className="tw-text-sm tw-text-gray-500  tw-flex tw-items-start tw-gap-1">
                <Icon
                  type="storefront"
                  className="tw-text-gray-500 !tw-w-4 !tw-h-4"
                />
                <span className="tw-line-clamp-2">
                  {item.network_name}
                </span>
              </div>
            )}
            {item.function_name && (
              <div className="tw-text-sm tw-text-gray-500  tw-flex tw-items-center tw-gap-1">
                <Icon
                  type="assignment_ind"
                  className="tw-text-gray-500 !tw-w-4 !tw-h-4"
                />
                <span className="tw-line-clamp-1">
                  {item.function_name}
                </span>
              </div>
            )}
            {getUserRender()}
          </>
        );
      case EEventTypes.EXCHANGE:
        const exchangeEvent = item as any;
        return (
          <>
            <div className="tw-flex tw-justify-between tw-items-center tw-gap-2">
              <div
                className={`${titleStyle}`}
              >
                {title}
              </div>
              <div className="tw-relative">
                {getUserRender()}
                {icon}
              </div>
            </div>
            {exchangeEvent.created_for && (
              <div className="tw-text-sm tw-text-gray-500  tw-flex tw-items-start  tw-gap-1">
                <Icon
                  type="storefront"
                  className="tw-text-gray-500 !tw-w-4 !tw-h-4"
                />
                <span className="tw-line-clamp-2 tw-leading-[1.5]">
                  {exchangeEvent.created_for}
                </span>
              </div>
            )}
            {exchangeEvent.function_name && (
              <div className="tw-text-sm tw-text-gray-500  tw-flex tw-items-center tw-gap-1">
                <Icon
                  type="assignment_ind"
                  className="tw-text-gray-500 !tw-w-4 !tw-h-4"
                />
                <span className="tw-line-clamp-1">
                  {exchangeEvent.function_name}
                </span>
              </div>
            )}
            <div className="tw-text-sm tw-flex tw-gap-1 tw-items-center">
              {statusIcon}
              <span className="tw-text-gray-500">{t(`calendar:${exchangeEvent.status.toLowerCase()}`)}</span>
            </div>
          </>
        );
      default:
        return (
          <>
            <div className={titleStyle}>{title}</div>
            {getUserRender()}
          </>
        );
    }
  };

  return (
    <div
      className={`CalanderExchangeItem tw-w-full -tw-space-y-2 tw-p-2 tw-cursor-pointer 
        tw-rounded-md tw-text-black tw-overflow-hidden tw-min-h-[60px] tw-flex tw-flex-col ${className}`}
      onClick={handleClick}
    >
      <div className="tw-flex tw-flex-col tw-gap-2 tw-flex-1 tw-justify-start">
        {getItemsRender()}
      </div>
    </div>
  );
}

export default memo(EventItem);
